function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import clsx from 'clsx';
import React, { useState, useEffect, useRef, useMemo, useCallback, useImperativeHandle } from 'react';
import findKey from 'lodash/findKey';
import { DropMenuReact } from 'common/components/drop-menu';
import { SvgIconReact, SvgIconType } from 'common/components/svg-icon';
import { TooltipReact } from 'common/components/tooltip';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { Translatable } from 'common/i18n';
import { useTranslation } from 'common/i18n/hooks';
import { LabelTheme, SectionItem } from 'common/components/items-menu/types';
import { ItemsMenuReact } from 'common/components/items-menu';
import { Directions } from 'common/components/drop-menu/types';
import { StackableComponent, Visibility } from 'common/components/base/stackable';
import { DisplaySize, Placement } from 'common/components/tooltip/types';
import { withStyles } from 'common/util/react';
import { queryBuilderUtils } from '../query-builder/util';
import { truncateMiddle } from '../cohorts-query-builder/util';
import { MAX_LABEL_LENGTH, QueryBlockTheme, SelectorType } from './constants';
import css from './stylesheets/index.inline.styl';
type HeaderSegmentType = {
  type: `text`;
  value: string;
} | {
  type: `select`;
  items: SectionItem[];
  selectedItem: SectionItem;
};
type Props = {
  autofocus?: boolean;
  /** Native browser tooltip text when hovered. */
  browserTooltip?: Translatable;
  buttonSize?: ButtonSize;
  canOpenMenu?: boolean;
  className?: string;
  /** The context panel to show for the selected item when hovered. */
  contextPanelElement?: React.ReactNode;
  disabled?: boolean;
  dropMenuContainerClass?: string;
  elref?: string;
  /** Items to display in the header. */
  header?: string | HeaderSegmentType[];
  helpText?: Translatable;
  inputOnly?: boolean;
  /** Adds an asterisk and italics. */
  isUnsaved?: boolean;
  /** Text shown on the query block. Will be middle-truncated if too long. */
  label: Translatable;
  /** Custom element for the label. */
  labelElement?: React.ReactNode;
  /** Highlights parts of the label. */
  labelHighlightSelections?: string[];
  leftIcon?: SvgIconType;
  menuAlignment?: Directions;
  /** Renders the drop menu contents, if any. */
  menuElement?: React.ReactNode;
  menuHasCustomWidth?: boolean;
  menuOffset?: number;
  onChangeOpen?: (open: boolean) => void;
  onClick?: () => void;
  onClickRemove?: () => void;
  onLabelMouseEnter?: () => void;
  onLabelMouseLeave?: () => void;
  onSelectHeaderItem?: (item: SectionItem) => void;
  /** Called when the context panel is shown on a closed query block. */
  onShowContextPanel?: () => void;
  /** Controls the menu's open state. */
  open?: boolean;
  /** Text that shows before the label. */
  prefixLabel?: Translatable;
  /** Text that shows after the event label, used for behavior prefixes like "AND, OR, THEN, etc.." */
  suffixLabel?: Translatable;
  preventClose?: boolean;
  preventCloseSelectors?: string[];
  readonly?: boolean;
  /** Whether a remove button is shown. */
  removable?: boolean;
  rightIcon?: SvgIconType;
  // todo: can this be consolidated into a single tooltip? why does it need to be on selector specifically?
  selectorTooltipElement?: React.ReactNode;
  selectorType?: SelectorType;
  tabIndex?: number;
  theme?: QueryBlockTheme;
  tooltip?: React.ComponentProps<typeof TooltipReact>;
};

/**
 * Provides the generic looks and behavior for a query block.
 *
 * This component should be pure and menu-agnostic.
 *
 * ==================================================================================
 * [leftIcon] [prefixLabel] [label or labelElement] [rightIcon] |      [removeButton]
 * ==================================================================================
 *                              [menuElement] (in DropMenu)
 */
export const NewQueryBlock = withStyles(React.forwardRef<HTMLDivElement, Props>((_ref, ref) => {
  var _ref2;
  let {
    autofocus,
    browserTooltip,
    buttonSize = ButtonSize.Medium,
    canOpenMenu = true,
    className,
    contextPanelElement,
    disabled,
    dropMenuContainerClass,
    elref,
    header,
    helpText,
    inputOnly,
    isUnsaved,
    label,
    labelElement,
    labelHighlightSelections = [],
    leftIcon,
    menuAlignment = Directions.Down,
    menuElement,
    menuHasCustomWidth,
    menuOffset = 0,
    onChangeOpen,
    onClick,
    onClickRemove,
    onLabelMouseEnter,
    onLabelMouseLeave,
    onSelectHeaderItem,
    onShowContextPanel,
    open,
    prefixLabel,
    suffixLabel,
    preventClose,
    preventCloseSelectors = [],
    readonly,
    removable,
    rightIcon,
    selectorTooltipElement,
    selectorType = SelectorType.Text,
    tabIndex,
    theme = ButtonTheme.Primary,
    tooltip
  } = _ref;
  const rootEl = useRef<HTMLDivElement>(null);
  const contextPanelVisibilityTimerId = useRef<NodeJS.Timeout>(null);
  const menuClosedTimestamp = useRef<number>();
  useImperativeHandle(ref, () => rootEl.current);
  const {
    print
  } = useTranslation();
  const [showContextPanel, setShowContextPanel] = useState(false);
  const selectorTheme = useMemo(() => {
    let newTheme = theme;
    const isDefaultTheme = theme === ButtonTheme.Primary;
    const buttonTheme = findKey(ButtonTheme, value => value === newTheme);

    // validate button theme and use QueryBlockTheme.Secondary as a default
    if (selectorType === SelectorType.Button) {
      if (!buttonTheme) {
        throw Error("Unknown button theme");
      }
      newTheme = isDefaultTheme ? QueryBlockTheme.Secondary : newTheme;
    }
    if (readonly && newTheme !== QueryBlockTheme.QueryBuilder) {
      newTheme = QueryBlockTheme.Tertiary;
    }
    return newTheme;
  }, [theme, selectorType, readonly]);
  const clearContextPanelVisibilityTimer = () => {
    if (contextPanelVisibilityTimerId.current) {
      clearTimeout(contextPanelVisibilityTimerId.current);
    }
    contextPanelVisibilityTimerId.current = null;
  };

  // cleanup visibility timer when we un-mount
  useEffect(() => {
    return () => {
      clearContextPanelVisibilityTimer();
    };
  }, []);
  const openMenu = useCallback(() => {
    if (open) {
      return;
    }
    onChangeOpen === null || onChangeOpen === void 0 || onChangeOpen(true);
    setShowContextPanel(false);
  }, [onChangeOpen, open]);
  const closeMenu = useCallback(() => {
    onChangeOpen === null || onChangeOpen === void 0 || onChangeOpen(false);
    setShowContextPanel(false);
    menuClosedTimestamp.current = Date.now();
  }, [onChangeOpen]);
  const delayShowContextPanel = () => {
    clearContextPanelVisibilityTimer();
    contextPanelVisibilityTimerId.current = setTimeout(() => {
      if (open) {
        return;
      }
      setShowContextPanel(true);
      onShowContextPanel === null || onShowContextPanel === void 0 || onShowContextPanel();
    }, 375);
  };

  // Delay closing the context panel so that when moving the mouse from the
  // query block to the context panel, the context doesn't get closed.
  const delayHideContextPanel = () => {
    clearContextPanelVisibilityTimer();
    contextPanelVisibilityTimerId.current = setTimeout(() => {
      if (open) {
        return;
      }
      setShowContextPanel(false);
      rootEl.current.dispatchEvent(new CustomEvent("closeContextMenu", {
        detail: {
          isOpen: false
        },
        bubbles: true
      }));
    }, 100);
  };
  const handleLabelMouseEnter = () => {
    // This is a a heuristic to prevent the following behavior: The user click the mp-query-block button and then
    // selects an item with just keyboard shortcuts. When the mp-items-menu is closed (because the mouse is still
    // on top of the mp-query-block button) this mouseenter event handler will retrigger causing
    // the context panel to be shown. I can't think of a better way to tell detect the scenario above, so using
    // the timestamp of closing menu as a heuristic to prevent reshowing the context panel within that timeframe.
    const menuWasRecentlyClosed = Number.isFinite(menuClosedTimestamp.current) && Date.now() - menuClosedTimestamp.current < 250;
    const anotherMenuIsOpen = StackableComponent.getStackingContextDepthForElement(rootEl.current) + 1 <= StackableComponent.getCurrentStackingContextDepth();
    if (!open && !menuWasRecentlyClosed && !anotherMenuIsOpen) {
      delayShowContextPanel();
    }
    onLabelMouseEnter === null || onLabelMouseEnter === void 0 || onLabelMouseEnter();
  };
  const handleLabelMouseLeave = () => {
    delayHideContextPanel();
    onLabelMouseLeave === null || onLabelMouseLeave === void 0 || onLabelMouseLeave();
  };
  const handleMenuChange = ev => {
    const {
      state
    } = ev.detail;
    if (state === Visibility.Closed) {
      closeMenu();
    } else {
      clearContextPanelVisibilityTimer();
    }
  };
  const handleMouseDown = () => clearContextPanelVisibilityTimer();
  const handleContextPanelHoverIn = () => {
    rootEl.current.dispatchEvent(new CustomEvent("mouseEnterContextMenu", {
      bubbles: true
    }));
    clearContextPanelVisibilityTimer();
  };
  const handleContextPanelHoverOut = () => {
    rootEl.current.dispatchEvent(new CustomEvent("mouseLeaveContextMenu", {
      bubbles: true
    }));
    delayHideContextPanel();
  };
  const handleLabelClick = () => {
    if (!disabled && !readonly && canOpenMenu) {
      openMenu();
    }
  };
  const headerSegments: HeaderSegmentType[] = (_ref2 = typeof header === "string" ? [{
    type: "text",
    value: header
  }] : header) !== null && _ref2 !== void 0 ? _ref2 : [];
  const hasNewUI = queryBuilderUtils.isCoreReport();
  const showInput = !inputOnly;
  const localizedPrefixLabel = print(prefixLabel);
  const localizedSuffixLabel = print(suffixLabel);
  const truncatedLabel = truncateMiddle(print(label), MAX_LABEL_LENGTH);
  let tooltipElement = null;
  if (!open) {
    if (showContextPanel && contextPanelElement) {
      tooltipElement = <div onMouseEnter={handleContextPanelHoverIn} onMouseLeave={handleContextPanelHoverOut}>
              {contextPanelElement}
            </div>;
    } else if (tooltip) {
      tooltipElement = <TooltipReact size={DisplaySize.Small} placement={Placement.Right} {...tooltip} />;
    }
  }
  return <div ref={rootEl} className={clsx("mp-query-block", {
    'has-prefix-label': !!localizedPrefixLabel,
    'for-new-ui': hasNewUI,
    'has-header-segments': headerSegments.length
  }, "mp-query-theme-".concat(selectorTheme), className, theme)} elref={elref} tabIndex={tabIndex} onMouseDown={handleMouseDown} onClick={onClick}>
          <div className='mp-query-block-header-container'>
            {headerSegments.map((segment, idx) => <HeaderSegment key={idx} segment={segment} onSelectHeaderItem={onSelectHeaderItem} />)}
            {helpText && <div className='header-info'>
                <SvgIconReact icon='info' />
                <TooltipReact className='help-text'>{print(helpText)}</TooltipReact>
              </div>}
          </div>
          {selectorType !== "button" && localizedPrefixLabel && <div className='mp-query-block-unit' elref='blockUnitLabel'>
              {localizedPrefixLabel}
            </div>}
          <div className={clsx("mp-query-block-selector", {
      'mp-query-editing': showInput && open
    })} onClick={handleLabelClick} elref='selectorLabel'>
            {tooltipElement}
            <div className='mp-query-block-selector-inner' elref='selectorLabelInner' onMouseEnter={handleLabelMouseEnter} onMouseLeave={handleLabelMouseLeave}>
              <BlockSelector autofocus={autofocus && !open} isMenuOpen={open} showInput={showInput} selectorTheme={selectorTheme} prefixLabel={localizedPrefixLabel} suffixLabel={localizedSuffixLabel} removable={removable} browserTooltipLabel={browserTooltip} label={truncatedLabel} leftIcon={leftIcon} rightIcon={rightIcon} onClickRemove={ev => {
          ev.stopPropagation();
          onClickRemove === null || onClickRemove === void 0 || onClickRemove();
        }} selectorType={selectorType} theme={theme as ButtonTheme} readonly={readonly || !canOpenMenu} buttonSize={buttonSize} disabled={disabled} labelElement={labelElement} isUnsaved={isUnsaved} highlightSelections={labelHighlightSelections} />
              {selectorTooltipElement}
            </div>
            {menuElement && <div className={clsx("mp-query-drop-menu-container", {
        'for-new-ui': hasNewUI
      }, dropMenuContainerClass)}>
                <DropMenuReact className={clsx({
          'custom-width': menuHasCustomWidth
        })} open={open} direction={menuAlignment} preventClose={preventClose} experimentalPositioning preventCloseSelectors={["#chmln", "mp-custom-property-modal", "mp-tooltip",
        // prevent clicks on tooltips from closing the menu, allows users to click links within tooltips
        ...preventCloseSelectors]}
        // TODO investigate further why 2+ immediate clicks locks out only properties menu,
        // for now don't close the opened properties menu on subsequent selector clicks
        {...hasNewUI ? {
          offset: menuOffset
        } : {}} onChange={handleMenuChange}>
                  {menuElement}
                </DropMenuReact>
              </div>}
          </div>
        </div>;
}), css);
function HeaderSegment(_ref3: {
  segment: HeaderSegmentType;
  onSelectHeaderItem?: (item: SectionItem) => void;
}) {
  let {
    segment,
    onSelectHeaderItem
  }: {
    segment: HeaderSegmentType;
    onSelectHeaderItem?: (item: SectionItem) => void;
  } = _ref3;
  const [open, setOpen] = useState(false);
  const {
    print
  } = useTranslation();
  if (segment.type === "text") {
    return <div className='mp-query-block-header-segment-label'>{segment.value}</div>;
  }
  if (segment.type === "select") {
    return <>
        <div className='mp-query-block-header-drop-menu'>
          <DropMenuReact open={open} onChange={ev => {
          if (ev.detail.state !== "open") {
            setOpen(false);
          }
        }}>
            <ItemsMenuReact sections={[{
            // format for mp-items-menu
            items: segment.items.map(item => _objectSpread(_objectSpread({}, item), {}, {
              isSelected: item.value === segment.selectedItem.value,
              labelTheme: LabelTheme.Strong
            }))
          }]} onClickItem={ev => {
            setOpen(false);
            onSelectHeaderItem === null || onSelectHeaderItem === void 0 || onSelectHeaderItem(ev.detail.item);
          }} />
          </DropMenuReact>
        </div>
        <div className='mp-query-block-header-segment-selector-label' onClick={() => setOpen(() => !open)}>
          {print(segment.selectedItem.label)}
          <SvgIconReact icon='caret-down' />
        </div>
      </>;
  }
  return null;
}
NewQueryBlock.displayName = "NewQueryBlock";
function SelectorLabel(_ref4: {
  label: string;
  highlightSelections: string[];
  isUnsaved: boolean;
}) {
  let {
    label,
    highlightSelections,
    isUnsaved
  }: {
    label: string;
    highlightSelections: string[];
    isUnsaved: boolean;
  } = _ref4;
  // if selections are given, highlight them
  if (highlightSelections.length) {
    const indices = new Set();
    const words = label.split(" ");
    for (const selection of highlightSelections) {
      const targetWords = selection.split(" ");
      for (let i = 0; i < words.length; i++) {
        if (words.slice(i, i + targetWords.length).join(" ") === targetWords.join(" ")) {
          for (let j = i; j < i + targetWords.length; j++) {
            indices.add(j);
          }
          break;
        }
      }
    }
    return <span className={clsx({
      'is-unsaved': isUnsaved
    })}>
        {words.map((word, idx) => {
        if (indices.has(idx)) {
          return <b key={"".concat(word, "-").concat(idx)}>{word} </b>;
        }
        return "".concat(word, " ");
      })}
      </span>;
  }
  return <span id='blockSelector' className={clsx({
    'is-unsaved': isUnsaved
  })} data-sentry-component="SelectorLabel" data-sentry-source-file="new-query-block.tsx">
      {label + (isUnsaved ? "*" : "")}
    </span>;
}
function BlockSelector(props: {
  autofocus: boolean;
  isMenuOpen: boolean;
  selectorTheme: QueryBlockTheme;
  prefixLabel: string;
  suffixLabel: string;
  removable: boolean;
  showInput: boolean;
  readonly: boolean;
  label: Translatable;
  browserTooltipLabel: Translatable;
  leftIcon: SvgIconType;
  disabled: boolean;
  rightIcon: SvgIconType;
  selectorType: SelectorType;
  theme: ButtonTheme;
  buttonSize: ButtonSize;
  isUnsaved: boolean;
  highlightSelections: string[];
  labelElement?: React.ReactNode;
  onClickRemove: (ev: React.MouseEvent) => void;
}) {
  const {
    print
  } = useTranslation();
  const label = print(props.label);
  const buttonRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (props.autofocus && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []);
  const removeButtonEl = props.removable && <div className='mp-query-block-remove-button' onClick={props.onClickRemove}>
      <SvgIconReact icon='circle-clear' />
    </div>;
  const rightIconEl = props.rightIcon && <SvgIconReact elref='rightIcon' icon={props.rightIcon} />;

  // TODO: the button theme type should be better separated from the query block theme type
  const allowedButtonThemes = [ButtonTheme.Select, ButtonTheme.QbSubitem];
  if (props.selectorType === "button" || allowedButtonThemes.includes(props.theme)) {
    return <ButtonReact ref={buttonRef as any} className={clsx("truncated", {
      ["icon-".concat(props.leftIcon)]: !!props.leftIcon
    })} disabled={props.disabled} icon={props.leftIcon || ""} theme={props.selectorTheme as ButtonTheme} size={props.buttonSize} notFocusable={props.readonly} pressed={props.isMenuOpen} readonly={props.readonly} elref='blockMenuLabelIcon' {...props.browserTooltipLabel ? {
      title: print(props.browserTooltipLabel)
    } : {}}>
        <div className={clsx("block-menu-label", {
        'has-right-icon': !!props.rightIcon
      })} elref='blockMenuLabel'>
          {props.labelElement || <>
              {props.prefixLabel && <span className='label-prefix'>{props.prefixLabel}</span>}
              <SelectorLabel label={label || ""} highlightSelections={props.highlightSelections} isUnsaved={props.isUnsaved} />
              {props.suffixLabel && <span className='label-prefix'>{props.suffixLabel}</span>}
            </>}
        </div>
        {rightIconEl}
        {removeButtonEl}
      </ButtonReact>;
  } else {
    // block label
    return <div className={clsx("mp-query-menu-label", "block-menu-label", {
      'is-disabled': props.disabled,
      'is-editing': props.showInput && props.isMenuOpen,
      ["is-theme-".concat(props.selectorTheme)]: true
    })} elref='blockMenuLabel'>
        {props.leftIcon && <SvgIconReact icon={props.leftIcon as any} elref='blockMenuLabelIcon' />}
        {label}
        {rightIconEl}
        {removeButtonEl}
      </div>;
  }
}